import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/**
 * Reusable, executive-tone layout for the three service pages.
 * All visual tokens (navy background, muted gold, Cormorant Garamond
 * headings, uppercase eyebrows, hairline dividers) match the homepage.
 */
export default function ServicePageLayout({
  eyebrow,
  title,
  intro,
  areasTitle = "Leadership Areas",
  areas = [],
  deepTitle,
  deepBody = [],
  ctaLabel,
  ctaHref = "/#contact",
  testId,
}) {
  return (
    <main
      data-testid={testId}
      className="bg-[#0E2750] text-white min-h-screen"
    >
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-[#C9A876]/[0.05] blur-[180px]" />
        <div className="pointer-events-none absolute bottom-0 -left-20 h-[420px] w-[420px] rounded-full bg-[#1d3b6e]/40 blur-[160px]" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to="/"
            data-testid="service-back-link"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-300/70 hover:text-[#C9A876] transition-colors mb-12"
          >
            <ArrowLeft size={13} strokeWidth={1.5} /> Back to ArcPoint
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="block h-[1px] w-10 bg-[#C9A876]/80" />
            <span className="font-mono-eyebrow text-[#C9A876]">{eyebrow}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display text-4xl sm:text-5xl lg:text-[76px] tracking-tight leading-[1.02] text-white max-w-4xl"
          >
            {title}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-10 max-w-3xl space-y-6 text-base md:text-lg leading-relaxed text-slate-200/85"
          >
            {intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Leadership Areas */}
      {areas.length > 0 && (
        <section className="relative bg-[#0E2750] py-20 md:py-28 border-y border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 md:mb-16">
              <div className="lg:col-span-5">
                <span className="font-mono-eyebrow text-[#C9A876]">◆ Practice Areas</span>
                <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white">
                  {areasTitle}
                </h2>
              </div>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
              {areas.map((a, i) => (
                <motion.li
                  key={a}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                  className="bg-[#0E2750] px-7 py-8 flex items-baseline gap-5 hover:bg-white/[0.025] transition-colors duration-500"
                  data-testid={`service-area-${i}`}
                >
                  <span className="font-mono-eyebrow text-slate-500 min-w-[24px]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-xl md:text-2xl text-white tracking-tight leading-snug">
                    {a}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Deep-narrative section */}
      {deepTitle && (
        <section className="relative bg-[#0A1F40] py-24 md:py-32">
          <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
            >
              <div className="lg:col-span-5 lg:sticky lg:top-32">
                <span className="font-mono-eyebrow text-[#C9A876]">◆ Approach</span>
                <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white">
                  {deepTitle}
                </h2>
                <div className="hairline mt-10" />
              </div>
              <div className="lg:col-span-7 space-y-6 text-base md:text-lg leading-relaxed text-slate-200/85">
                {deepBody.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative bg-[#0E2750] py-24 md:py-32 border-t border-white/[0.06] overflow-hidden">
        <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[360px] w-[820px] rounded-full bg-[#C9A876]/[0.05] blur-[160px]" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <span className="font-mono-eyebrow text-[#C9A876]">◆ Next step</span>
          <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white">
            Let's build something{" "}
            <span className="font-display-italic text-[#C9A876]">exceptional</span>.
          </h2>
          <p className="mt-6 text-slate-300/80 max-w-xl mx-auto leading-relaxed">
            Every ArcPoint conversation is handled personally and with complete discretion.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to={ctaHref}
              data-testid="service-cta"
              className="group inline-flex items-center gap-2 px-7 py-4 bg-[#C9A876] hover:bg-[#DCC298] text-[#0E2750] text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors duration-500"
            >
              {ctaLabel}
              <ArrowUpRight
                size={14}
                strokeWidth={1.8}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
            <Link
              to="/"
              data-testid="service-secondary-cta"
              className="text-[11px] uppercase tracking-[0.22em] text-slate-300/70 hover:text-[#C9A876] transition-colors"
            >
              Return to ArcPoint →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
